import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit3, Save, X, Users, Tag, Percent,
  CheckCircle2, AlertTriangle, RefreshCw, ToggleLeft, ToggleRight, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATS = [
  { id: 'fc_3d_designer', name: '3D Designer', commission: 4.0, active: true },
  { id: 'fc_graphic_designer', name: 'Graphic Designer', commission: 4.0, active: true },
  { id: 'fc_event_coord', name: 'Event Coordinator', commission: 4.5, active: true },
  { id: 'fc_project_mgr', name: 'Project Manager', commission: 5.0, active: true },
  { id: 'fc_electrician', name: 'Electrician', commission: 3.5, active: true },
  { id: 'fc_carpenter', name: 'Carpenter', commission: 3.5, active: true },
  { id: 'fc_photographer', name: 'Photographer', commission: 4.0, active: true },
];

export default function UltraFreelancers() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', commission: '' });
  const [newCat, setNewCat] = useState({ name: '', commission: '' });
  const [addingNew, setAddingNew] = useState(false);
  const [freelancers, setFreelancers] = useState([]);
  const [flLoading, setFlLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/ultra/freelancer-categories`, { headers });
      setCategories(res.data?.categories || DEFAULT_CATS);
    } catch {
      setCategories(DEFAULT_CATS);
    } finally { setLoading(false); }
  };

  const fetchFreelancers = async () => {
    setFlLoading(true);
    try {
      const res = await axios.get(`${API}/public/freelancers?limit=50`, { headers });
      setFreelancers(res.data?.items || []);
    } catch { setFreelancers([]); }
    finally { setFlLoading(false); }
  };

  useEffect(() => { fetchCategories(); fetchFreelancers(); }, []);

  const saveCategories = async (cats) => {
    setSaving(true);
    try {
      await axios.put(`${API}/ultra/freelancer-categories`, { categories: cats }, { headers });
      toast.success('Kategoriler kaydedildi');
      setCategories(cats);
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, commission: String(cat.commission) });
  };

  const saveEdit = () => {
    if (!editForm.name.trim()) { toast.error('Kategori adı gerekli'); return; }
    const comm = parseFloat(editForm.commission);
    if (isNaN(comm) || comm < 0 || comm > 100) { toast.error('Geçerli komisyon oranı girin (0-100)'); return; }
    const updated = categories.map(c => c.id === editingId ? { ...c, name: editForm.name.trim(), commission: comm } : c);
    saveCategories(updated);
    setEditingId(null);
  };

  const toggleActive = (id) => {
    const updated = categories.map(c => c.id === id ? { ...c, active: !c.active } : c);
    saveCategories(updated);
  };

  const deleteCategory = (id) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) return;
    const updated = categories.filter(c => c.id !== id);
    saveCategories(updated);
  };

  const addCategory = () => {
    if (!newCat.name.trim()) { toast.error('Kategori adı gerekli'); return; }
    const comm = parseFloat(newCat.commission);
    if (isNaN(comm) || comm < 0 || comm > 100) { toast.error('Geçerli komisyon oranı girin'); return; }
    const id = `fc_${newCat.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
    const already = categories.find(c => c.id === id);
    if (already) { toast.error('Bu isimde bir kategori zaten var'); return; }
    const updated = [...categories, { id, name: newCat.name.trim(), commission: comm, active: true }];
    saveCategories(updated);
    setNewCat({ name: '', commission: '' });
    setAddingNew(false);
  };

  const activeCount = categories.filter(c => c.active !== false).length;
  const avgCommission = categories.length ? (categories.reduce((sum, c) => sum + (c.commission || 0), 0) / categories.length).toFixed(1) : '—';

  const filteredFreelancers = freelancers.filter(f =>
    !search ||
    f.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    f.category_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Freelancer Yönetimi</h1>
          <p className="text-gray-500 text-sm mt-0.5">Kategoriler, komisyon oranları ve kayıtlı freelancer'lar</p>
        </div>
        <Button onClick={() => setAddingNew(true)} className="bg-red-600 hover:bg-red-700 text-white gap-2">
          <Plus className="w-4 h-4" />Kategori Ekle
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Tag, label: 'Aktif Kategori', value: activeCount, color: 'bg-red-50 text-red-600' },
          { icon: Users, label: 'Kayıtlı Freelancer', value: freelancers.length, color: 'bg-blue-50 text-blue-600' },
          { icon: Percent, label: 'Ort. Komisyon', value: `%${avgCommission}`, color: 'bg-emerald-50 text-emerald-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-black text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categories */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <Tag className="w-4 h-4 text-red-500" />Kategori Listesi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Add new form */}
            {addingNew && (
              <div className="mb-3 p-3 bg-red-50 rounded-xl border border-red-100 space-y-2">
                <p className="text-xs font-bold text-red-700">Yeni Kategori</p>
                <div className="flex gap-2">
                  <Input value={newCat.name} onChange={e => setNewCat(p => ({ ...p, name: e.target.value }))}
                    placeholder="Kategori adı" className="flex-1 text-sm h-9" />
                  <Input type="number" min="0" max="100" step="0.5"
                    value={newCat.commission} onChange={e => setNewCat(p => ({ ...p, commission: e.target.value }))}
                    placeholder="Komisyon %" className="w-28 text-sm h-9" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={addCategory} size="sm" className="bg-red-600 hover:bg-red-700 text-white flex-1">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Ekle
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setAddingNew(false); setNewCat({ name: '', commission: '' }); }} className="flex-1">
                    İptal
                  </Button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-red-400" />
              </div>
            ) : (
              <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
                {categories.map(cat => (
                  <div key={cat.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all
                      ${cat.active === false ? 'bg-gray-50 border-gray-100 opacity-60' : 'bg-white border-gray-100 hover:border-red-100'}`}>
                    {editingId === cat.id ? (
                      <>
                        <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                          className="flex-1 text-xs h-8" />
                        <Input type="number" min="0" max="100" step="0.5"
                          value={editForm.commission} onChange={e => setEditForm(p => ({ ...p, commission: e.target.value }))}
                          className="w-20 text-xs h-8" />
                        <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-800 p-1">
                          <Save className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 p-1">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => toggleActive(cat.id)} className="shrink-0">
                          {cat.active !== false
                            ? <ToggleRight className="w-5 h-5 text-emerald-500" />
                            : <ToggleLeft className="w-5 h-5 text-gray-300" />}
                        </button>
                        <span className="flex-1 text-sm text-gray-800 font-medium">{cat.name}</span>
                        <Badge className="text-[10px] bg-orange-50 text-orange-600 border border-orange-200">
                          %{cat.commission}
                        </Badge>
                        <button onClick={() => startEdit(cat)} className="text-gray-400 hover:text-blue-600 p-1 transition-colors">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteCategory(cat.id)} className="text-gray-400 hover:text-red-600 p-1 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Freelancers List */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />Kayıtlı Freelancer'lar
              </CardTitle>
              <button onClick={fetchFreelancers} className="text-gray-400 hover:text-gray-600 transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="İsim veya kategori ara..." className="mt-2 text-sm h-9" />
          </CardHeader>
          <CardContent>
            {flLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
              </div>
            ) : filteredFreelancers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">
                  {search ? 'Arama sonucu bulunamadı' : 'Henüz kayıtlı freelancer yok'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {filteredFreelancers.map(f => (
                  <div key={f.org_id} className="flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-100 hover:border-blue-100 bg-white">
                    {f.avatar_url ? (
                      <img src={f.avatar_url} alt={f.full_name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                        <span className="text-red-500 text-xs font-bold">{f.full_name?.[0] || 'F'}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold text-gray-900 truncate">{f.full_name}</p>
                        {f.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                      </div>
                      <p className="text-xs text-red-500">{f.category_name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium
                        ${f.availability === 'available' ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                        : f.availability === 'busy' ? 'bg-amber-50 text-amber-600 border-amber-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                        {f.availability === 'available' ? 'Müsait' : f.availability === 'busy' ? 'Meşgul' : 'Kapalı'}
                      </span>
                      <a href={`/freelancer/${f.slug}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:text-blue-700">Profil</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info box */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Komisyon Oranı Hakkında</p>
          <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
            Komisyon oranı, her başarılı iş sonucunda platformun kazandığı yüzdedir. Örneğin %4 komisyon oranıyla,
            freelancer 1000$ kazanırsa platform 40$ alır. Oranlar kategori bazında özelleştirilebilir.
          </p>
        </div>
      </div>
    </div>
  );
}
